import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Shield, DollarSign, ArrowDownCircle, ArrowUpCircle, 
  TrendingUp, Settings, UserCog, FileText, Menu, X, LogOut, ChevronDown, MapPin, Wallet, Database, MessageSquare,
  KeyRound, Activity, ArrowLeft
} from 'lucide-react';

import { LogoCompact } from '../../components/Logo';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}');
  const adminRole = adminInfo.role || 'super_admin';

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin', 'finance_admin', 'support_admin', 'trading_admin'] },
    { path: '/admin/users', icon: Users, label: 'Users', roles: ['super_admin', 'finance_admin', 'support_admin', 'trading_admin'] },
    { path: '/admin/kyc', icon: Shield, label: 'KYC Management', roles: ['super_admin', 'support_admin'] },
    { path: '/admin/deposits', icon: ArrowDownCircle, label: 'Deposits', roles: ['super_admin', 'finance_admin'] },
    { path: '/admin/withdrawals', icon: ArrowUpCircle, label: 'Withdrawals', roles: ['super_admin', 'finance_admin'] },
    { path: '/admin/trading', icon: TrendingUp, label: 'Trading Control', roles: ['super_admin', 'trading_admin'] },
    { path: '/admin/deposit-addresses', icon: MapPin, label: 'Deposit Addresses', roles: ['super_admin', 'finance_admin'] },
    { path: '/admin/settings', icon: Settings, label: 'Settings', roles: ['super_admin'] },
    { path: '/admin/admins', icon: UserCog, label: 'Admin Users', roles: ['super_admin'] },
    { path: '/admin/audit', icon: FileText, label: 'Audit Logs', roles: ['super_admin', 'support_admin'] },
    { path: '/admin/legacy-audit', icon: KeyRound, label: 'Legacy Auth Audit', roles: ['super_admin', 'support_admin'] },
    { path: '/admin/balance-sync', icon: Activity, label: 'Balance Sync', roles: ['super_admin', 'finance_admin'] },
    { path: '/admin/system-audit', icon: Database, label: 'System Audit', roles: ['super_admin'] },
    { path: '/admin/live-chat', icon: MessageSquare, label: 'Live Chat', roles: ['super_admin', 'support_admin'] },
  ];

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(adminRole));

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:transform-none ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <LogoCompact size={36} />
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Phantom Trades</h1>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-6 py-3 text-left transition-colors ${
                    isActive(item.path)
                      ? 'bg-purple-600 text-white border-r-4 border-purple-400'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-semibold">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Admin Info */}
          <div className="border-t border-gray-800 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
                <span className="text-white font-bold">{adminInfo.email?.[0].toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{adminInfo.name || 'Admin'}</p>
                <p className="text-xs text-gray-500">{adminRole.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-gray-900 border-b border-gray-800">
          <div className="flex items-center justify-between px-4 lg:px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex-1 lg:ml-0 ml-4">
              <h2 className="text-xl font-bold text-white">
                {filteredMenuItems.find(item => isActive(item.path))?.label || 'Admin Panel'}
              </h2>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <span className="text-white font-semibold hidden sm:block">{adminInfo.name || 'Admin'}</span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-2 px-4 py-3 hover:bg-gray-700 text-red-400 rounded-lg"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm mb-4"
            data-testid="admin-back-button"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
          <Outlet />
        </main>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        />
      )}
    </div>
  );
};

export default AdminLayout;